/**
 * Concept Comparison View Component
 *
 * Table/swimlane layout showing concepts grouped by category with
 * cross-language implementation comparison. Optimized for the 80% use case:
 * "I know language X, what's the equivalent in language Y?"
 *
 * @module concept-graph/components
 */

import React, { useState, useMemo } from 'react';
import { Code2, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import type { Concept, ConceptCategory } from '../types';
import { getCategoryColor } from '../utils/graph-layout';

export interface ConceptComparisonViewProps {
  concepts: Concept[];
  selectedConceptId?: string | null;
  onConceptClick: (conceptId: string) => void;
  onViewGraph?: (conceptId: string) => void;
  availableLanguages?: string[];
  visibleLanguages?: string[];
  className?: string;
}

interface GroupedConcepts {
  [category: string]: Concept[];
}

/**
 * Comparison view showing concepts in table format with language implementations
 */
export function ConceptComparisonView({
  concepts,
  selectedConceptId,
  onConceptClick,
  onViewGraph,
  availableLanguages = ['C', 'Rust', 'Go'],
  visibleLanguages = ['C', 'Rust', 'Go'],
  className = ''
}: ConceptComparisonViewProps) {
  const [expandedConcepts, setExpandedConcepts] = useState<Set<string>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set([
    'memory_management', 'control_flow', 'data_structures', 'functions', 'types'
  ]));

  // Group concepts by category
  const groupedConcepts = useMemo(() => {
    const groups: GroupedConcepts = {};
    concepts.forEach(concept => {
      if (!groups[concept.category]) {
        groups[concept.category] = [];
      }
      groups[concept.category].push(concept);
    });

    // Sort concepts within each group by difficulty
    Object.keys(groups).forEach(category => {
      groups[category].sort((a, b) => a.difficulty - b.difficulty);
    });

    return groups;
  }, [concepts]);

  const toggleConcept = (conceptId: string) => {
    setExpandedConcepts(prev => {
      const next = new Set(prev);
      if (next.has(conceptId)) {
        next.delete(conceptId);
      } else {
        next.add(conceptId);
      }
      return next;
    });
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const getCategoryLabel = (category: ConceptCategory): string => {
    const labels: Record<ConceptCategory, string> = {
      memory_management: 'Memory Management',
      control_flow: 'Control Flow',
      data_structures: 'Data Structures',
      functions: 'Functions',
      types: 'Types',
      concurrency: 'Concurrency',
      io: 'I/O',
      error_handling: 'Error Handling',
      modules: 'Modules',
      metaprogramming: 'Metaprogramming',
      design_patterns: 'Design Patterns',
      paradigms: 'Paradigms'
    };
    return labels[category] || category;
  };

  const getDifficultyStars = (difficulty: number): string => {
    return '★'.repeat(difficulty);
  };

  return (
    <div className={`h-full overflow-auto bg-warm-bg ${className}`}>
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Concept Comparison</h1>
          <p className="text-sm text-slate-600">
            Compare programming concepts across languages. Click any concept to see details.
          </p>
        </div>

        {/* Language Legend */}
        <div className="mb-6 flex items-center gap-4 text-sm">
          <span className="text-slate-600 font-medium">Languages:</span>
          {visibleLanguages.map(lang => (
            <div key={lang} className="flex items-center gap-2 px-3 py-1 bg-white rounded-lg border border-slate-200">
              <Code2 className="w-4 h-4 text-orange-500" />
              <span className="font-medium text-slate-700">{lang}</span>
            </div>
          ))}
        </div>

        {/* Comparison Table by Category */}
        {Object.entries(groupedConcepts).map(([category, categoryConcepts]) => {
          const isExpanded = expandedCategories.has(category);
          const categoryColor = getCategoryColor(category as ConceptCategory);

          return (
            <div key={category} className="mb-8">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category)}
                className="w-full flex items-center gap-3 mb-4 group"
              >
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                )}
                <div
                  className="w-1 h-6 rounded-full"
                  style={{ backgroundColor: categoryColor }}
                />
                <h2 className="text-lg font-semibold text-slate-900 group-hover:text-orange-600 transition-colors">
                  {getCategoryLabel(category as ConceptCategory)}
                </h2>
                <span className="text-sm text-slate-500">
                  ({categoryConcepts.length} concept{categoryConcepts.length !== 1 ? 's' : ''})
                </span>
              </button>

              {/* Concepts in Category */}
              {isExpanded && (
                <div className="space-y-3">
                  {categoryConcepts.map(concept => {
                    const isSelected = concept.id === selectedConceptId;
                    const isConceptExpanded = expandedConcepts.has(concept.id);

                    return (
                      <div
                        key={concept.id}
                        className={`bg-white rounded-lg border transition-all ${
                          isSelected
                            ? 'border-orange-500 shadow-md'
                            : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                        }`}
                      >
                        {/* Concept Header */}
                        <div className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <button
                                onClick={() => {
                                  onConceptClick(concept.id);
                                  toggleConcept(concept.id);
                                }}
                                className="text-left w-full group"
                              >
                                <div className="flex items-center gap-3">
                                  {isConceptExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                  )}
                                  <h3 className="font-semibold text-slate-900 group-hover:text-orange-600 transition-colors">
                                    {concept.name}
                                  </h3>
                                  <span className="text-orange-500 text-sm">
                                    {getDifficultyStars(concept.difficulty)}
                                  </span>
                                  {concept.layer && (
                                    <span className="text-xs text-slate-500 px-2 py-0.5 bg-slate-100 rounded">
                                      {concept.layer.replace(/_/g, ' ')}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-slate-600 mt-1 ml-7">
                                  {concept.description}
                                </p>
                              </button>
                            </div>

                            {onViewGraph && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onViewGraph(concept.id);
                                }}
                                className="ml-4 px-3 py-1 text-xs font-medium text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-lg transition-colors flex items-center gap-1"
                              >
                                View Graph
                                <ExternalLink className="w-3 h-3" />
                              </button>
                            )}
                          </div>

                          {/* Language Comparison Grid */}
                          <div className="grid gap-3 ml-7" style={{ gridTemplateColumns: `repeat(${visibleLanguages.length}, 1fr)` }}>
                            {visibleLanguages.map(lang => {
                              const impl = concept.implementations?.[lang];

                              if (!impl) {
                                return (
                                  <div key={lang} className="p-3 bg-slate-50 rounded border border-slate-100">
                                    <div className="text-xs font-medium text-slate-500 mb-1">{lang}</div>
                                    <div className="text-xs text-slate-400 italic">No implementation</div>
                                  </div>
                                );
                              }

                              const firstPattern = impl.syntaxPatterns?.[0];
                              const firstSnippet = impl.codeSnippets?.[0];

                              return (
                                <div key={lang} className="p-3 bg-slate-50 rounded border border-slate-200 hover:border-slate-300 transition-colors">
                                  <div className="text-xs font-semibold text-slate-700 mb-2">{lang}</div>

                                  {firstPattern && (
                                    <div className="mb-2">
                                      <code className="text-xs font-mono text-slate-800 bg-white px-2 py-1 rounded block overflow-x-auto">
                                        {firstPattern.syntax}
                                      </code>
                                      <p className="text-xs text-slate-600 mt-1">
                                        {firstPattern.explanation}
                                      </p>
                                    </div>
                                  )}

                                  {isConceptExpanded && firstSnippet && (
                                    <div className="mt-3 pt-3 border-t border-slate-200">
                                      <div className="text-xs font-medium text-slate-700 mb-1">
                                        {firstSnippet.title}
                                      </div>
                                      <code className="text-xs font-mono text-slate-800 bg-white px-2 py-1 rounded block overflow-x-auto whitespace-pre">
                                        {firstSnippet.code}
                                      </code>
                                      {firstSnippet.explanation && (
                                        <p className="text-xs text-slate-600 mt-2">
                                          {firstSnippet.explanation}
                                        </p>
                                      )}
                                    </div>
                                  )}

                                  {isConceptExpanded && impl.commonPitfalls && impl.commonPitfalls.length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-slate-200">
                                      <div className="text-xs font-medium text-red-700 mb-1">⚠️ Common Pitfalls:</div>
                                      <ul className="text-xs text-slate-600 space-y-1">
                                        {impl.commonPitfalls.slice(0, 2).map((pitfall, idx) => (
                                          <li key={idx} className="flex items-start gap-1">
                                            <span className="text-red-500 flex-shrink-0">•</span>
                                            <span>{pitfall}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {isConceptExpanded && concept.learningContext && (
                          <div className="px-4 pb-4 border-t border-slate-100">
                            <div className="mt-3 pt-3">
                              <div className="grid grid-cols-2 gap-4 text-xs">
                                {concept.learningContext.prerequisites && concept.learningContext.prerequisites.length > 0 && (
                                  <div>
                                    <div className="font-medium text-slate-700 mb-1">Prerequisites:</div>
                                    <div className="text-slate-600">
                                      {concept.learningContext.prerequisites.join(', ')}
                                    </div>
                                  </div>
                                )}
                                {concept.learningContext.leadsTo && concept.learningContext.leadsTo.length > 0 && (
                                  <div>
                                    <div className="font-medium text-slate-700 mb-1">Leads to:</div>
                                    <div className="text-slate-600">
                                      {concept.learningContext.leadsTo.join(', ')}
                                    </div>
                                  </div>
                                )}
                              </div>
                              {concept.learningContext.realWorldUse && (
                                <div className="mt-3">
                                  <div className="font-medium text-slate-700 mb-1">Real-world use:</div>
                                  <div className="text-slate-600">
                                    {concept.learningContext.realWorldUse}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ConceptComparisonView;
